import type { SalonStockerHttpTypes } from "@repo/api-sdk";

/**
 * The session's **actor type** - whether the person driving this browser is the
 * salon themselves or an operator shopping on their behalf.
 *
 * Both authenticate as the same Medusa customer, so nothing else distinguishes
 * them. Without this, an operator building a cart and the salon reviewing it
 * would be indistinguishable in analytics - and the pilot's whole question is
 * what the *salon* does.
 *
 * Stored in `localStorage` beside the session JWT because it has to survive the
 * redirect out of `/enter` and every subsequent reload.
 *
 * Two kinds of reader, with different needs:
 *
 * - **Analytics attribution** uses {@link getActorType}, which always answers
 *   (a missing or unreadable value reads as the salon). Attribution must never
 *   be able to break a page, and a wrong label costs a mislabelled event.
 * - **Routing decisions** (the first-login onboarding gate) use
 *   {@link readSessionActor}, which can say "unknown". A wrong guess there is
 *   not a mislabel: it would send a concierge operator to a form asking for the
 *   salon's address.
 *
 * Neither is trusted for authorization, and the server never reads it back. It
 * decides which screens a session is shown, never what it may do.
 */

const ACTOR_TYPE_STORAGE_KEY = "ss_actor_type";

export type ActorType = SalonStockerHttpTypes.ConciergeActorType;

/**
 * The default actor for anyone who did not arrive through a concierge link -
 * a salon logging in with a password is, by definition, the salon.
 */
export const DEFAULT_ACTOR_TYPE: ActorType = "salon_user";

const isActorType = (value: unknown): value is ActorType =>
  value === "salon_user" || value === "concierge_operator";

/**
 * The session's actor, or `null` when it cannot be established.
 *
 * - A stored, recognised value is the answer.
 * - **Nothing stored** is a salon: only a concierge redemption writes the key,
 *   and a password login or logout clears it, so a readable-but-empty store
 *   really does mean "not a concierge session".
 * - **Storage that throws** (Safari private mode, embedded webviews) or a
 *   **junk value** is `null`. We cannot tell a salon from an operator whose
 *   redemption was written somewhere we cannot read, and callers that route on
 *   the answer must choose their own safe default rather than inherit "salon".
 */
export function readSessionActor(): ActorType | null {
  try {
    const stored = window.localStorage.getItem(ACTOR_TYPE_STORAGE_KEY);
    if (stored === null) return DEFAULT_ACTOR_TYPE;
    return isActorType(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Read the stored actor for analytics attribution, falling back to the salon.
 *
 * Tolerates storage being unavailable and junk values - attribution must never
 * be able to break a page. Anything that routes on the actor should use
 * {@link readSessionActor} instead, which does not paper over "unknown".
 */
export function getActorType(): ActorType {
  return readSessionActor() ?? DEFAULT_ACTOR_TYPE;
}

/** Persist the actor a redemption reported. */
export function setActorType(actorType: ActorType): void {
  try {
    window.localStorage.setItem(ACTOR_TYPE_STORAGE_KEY, actorType);
  } catch {
    // Storage unavailable - the session still works, events just fall back to
    // the default actor.
  }
}

/**
 * Forget the stored actor. Called on logout so the next person on this browser
 * is not attributed to the previous session's actor.
 */
export function clearActorType(): void {
  try {
    window.localStorage.removeItem(ACTOR_TYPE_STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
